import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord } from '@warp-drive/utilities/json-api';
import { saveRecord } from 'tio-data/requests/save-record';
import type { Store } from '@warp-drive/core';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';

export default class AuthenticatedPslfRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;

  beforeModel() {
    // PSLF is an employee-scoped flow: child routes and templates
    // (dashboard, start-new, sign, etc.) all dereference model.pslfProfile
    // and model.pslfProfile.employee. If we have no current employee there
    // is no pslf profile to load — send them to their own landing route
    // instead of returning a null model that crashes further down the tree.
    // `landingRoute`, not 'authenticated': that resolves to an index with no
    // template, and an in-app transition never re-runs the parent's redirect.
    if (!this.sessionContext.currentEmployee?.id) {
      this.router.replaceWith(this.sessionContext.landingRoute);
    }
  }

  async model() {
    const currentEmployee = this.sessionContext.currentEmployee;
    // beforeModel above guarantees currentEmployee?.id is truthy.
    const { content: employeeContent } = (await this.store.request(
      findRecord<EmployeeModel>('employee', currentEmployee!.id!, {
        include: 'pslf-profile,pslf-forms,employee-addition',
        reload: true,
      })
    )) as { content: { data: EmployeeModel } };
    const employee = employeeContent.data;
    const companyId = employee?.company?.id as string;
    // Start company fetch in parallel with profile create (if needed)
    const companyPromise = this.store.request(
      findRecord<CompanyModel>('company', companyId, {
        include: 'pslf-groups',
        reload: true,
      })
    ) as Promise<{ content: { data: CompanyModel } }>;
    let profile = employee.pslfProfile;
    if (!profile) {
      profile = this.store.createRecord<PslfProfileModel>('pslf-profile', {
        employee,
      });
      await saveRecord(this.store, profile);
    }
    const { content: companyContent } = await companyPromise;
    const company = companyContent.data;
    return {
      pslfProfile: profile,
      groups: company.pslfGroups,
    };
  }
}
